import { useMutation, useQuery } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { IRatingForm, IRatingResponse } from '../../Interfaces/IRatingForm';

export interface IOpenFormulareStatus {
  isActive: boolean;
  isConfigured: boolean;
  isReady: boolean;
  missing: string[];
}

export const useGetOpenFormulareStatus = () => {
  const {
    data: ofStatus,
    isLoading: isLoadingOFStatus,
    refetch: refetchOFStatus,
  } = useQuery<IOpenFormulareStatus>({
    queryKey: ['openformulare', 'status'],
    queryFn: () => customFetcher({ endpoint: '/openformulare/status' }),
    // Bewusst kein staleTime: Mount-Refetch ist hier billig und vermeidet
    // einen "stale" Warning-Alert nach dem Konfigurieren der Erweiterung.
  });
  return { ofStatus, isLoadingOFStatus, refetchOFStatus };
};

export const useGetRatingForms = () => {
  const {
    data: ratingForms,
    isLoading: isLoadingRatingForms,
    refetch: refetchRatingForms,
  } = useQuery<IRatingForm[]>({
    queryKey: ['ratingform'],
    queryFn: () => customFetcher({ endpoint: '/ratingform' }),
  });
  return { ratingForms, isLoadingRatingForms, refetchRatingForms };
};

export const useGetRatingFormFullObject = (idRatingForm: number | null) => {
  const enabled = idRatingForm !== null && idRatingForm !== undefined;
  const {
    data: ratingForm,
    isLoading: isLoadingRatingForm,
    refetch: refetchRatingForm,
  } = useQuery<IRatingForm>({
    queryKey: ['ratingform', idRatingForm],
    queryFn: () =>
      customFetcher({ endpoint: `/ratingform/fullObject/${idRatingForm}` }),
    enabled,
  });
  return { ratingForm, isLoadingRatingForm, refetchRatingForm };
};

export const useCreateRatingForm = () => {
  const { mutateAsync: createRatingForm, isPending: isPendingCreate } =
    useMutation({
      mutationFn: (form: IRatingForm) =>
        customFetcher({
          endpoint: '/ratingform/fullObject',
          method: 'POST',
          body: form,
        }),
    });
  return { createRatingForm, isPendingCreate };
};

export const useUpdateRatingForm = () => {
  const { mutateAsync: updateRatingForm, isPending: isPendingUpdate } =
    useMutation({
      mutationFn: (form: IRatingForm) =>
        customFetcher({
          endpoint: '/ratingform/fullObject',
          method: 'PUT',
          body: form,
        }),
    });
  return { updateRatingForm, isPendingUpdate };
};

export const useDeleteRatingForm = () => {
  const { mutateAsync: deleteRatingForm, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (idRatingForm: number) =>
        customFetcher({
          endpoint: `/ratingform/${idRatingForm}`,
          method: 'DELETE',
        }),
    });
  return { deleteRatingForm, isPendingDelete };
};

export const useSendRating = () => {
  const { mutateAsync: sendRating, isPending: isPendingSend } = useMutation({
    mutationFn: (idLegalTransaction: number) =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/sendrating`,
        method: 'POST',
        body: {},
      }),
  });
  return { sendRating, isPendingSend };
};

export const useGetRatingResponsesForLegalTransaction = (
  idLegalTransaction: number | null
) => {
  const enabled =
    idLegalTransaction !== null && idLegalTransaction !== undefined;
  const {
    data: responses,
    isLoading: isLoadingResponses,
    refetch: refetchResponses,
  } = useQuery<IRatingResponse[]>({
    queryKey: ['ratingresponse', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/ratingresponse?idLegalTransaction=${idLegalTransaction}`,
      }),
    enabled,
  });
  return { responses, isLoadingResponses, refetchResponses };
};

export interface IRatingResponseFullObject extends IRatingResponse {
  Answers: Array<{
    idRatingAnswer: number;
    idRatingFormQuestion: number;
    AnswerNumeric: number | null;
    AnswerText: string | null;
    QuestionLabel?: string;
    QuestionType?: string;
    RangeMin?: number | null;
    RangeMax?: number | null;
  }>;
}

// ---------- Trigger-Override-APIs ----------
// Nur noch pro Vorgang (kein Vorgangsart-Override mehr).

export interface IRatingTriggerTransaction {
  idRatingFormTriggerOnTransaction?: number;
  idLegalTransaction: number;
  idRatingForm: number | null;
  TriggerType: 'never' | 'state' | 'phase' | 'requirement' | null;
  idLegalTransactionState: number | null;
  idLegalPhase: number | null;
  idLegalRequirement: number | null;
}

export interface IRatingTriggerEffective {
  scope: 'transaction' | 'default';
  idRatingForm: number | null;
  TriggerType: 'never' | 'state' | 'phase' | 'requirement';
  idLegalTransactionState?: number | null;
  idLegalPhase?: number | null;
  idLegalRequirement?: number | null;
}

export const useGetRatingTriggerByTransaction = (
  idLegalTransaction: number | null
) => {
  const enabled =
    idLegalTransaction !== null &&
    idLegalTransaction !== undefined &&
    idLegalTransaction > 0;
  const {
    data: triggerByTransaction,
    isLoading: isLoadingTriggerByTransaction,
    refetch: refetchTriggerByTransaction,
  } = useQuery<IRatingTriggerTransaction>({
    queryKey: ['ratingform', 'trigger', 'transaction', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/ratingform/trigger/transaction/${idLegalTransaction}`,
      }),
    enabled,
  });
  return {
    triggerByTransaction,
    isLoadingTriggerByTransaction,
    refetchTriggerByTransaction,
  };
};

export const useSetRatingTriggerByTransaction = () => {
  const {
    mutateAsync: setTriggerByTransaction,
    isPending: isPendingTriggerByTransaction,
  } = useMutation({
    mutationFn: (vars: {
      idLegalTransaction: number;
      body: Partial<IRatingTriggerTransaction>;
    }) =>
      customFetcher({
        endpoint: `/ratingform/trigger/transaction/${vars.idLegalTransaction}`,
        method: 'PUT',
        body: vars.body,
      }),
  });
  return { setTriggerByTransaction, isPendingTriggerByTransaction };
};

export const useDeleteRatingTriggerByTransaction = () => {
  const {
    mutateAsync: deleteTriggerByTransaction,
    isPending: isPendingDeleteTriggerByTransaction,
  } = useMutation({
    mutationFn: (idLegalTransaction: number) =>
      customFetcher({
        endpoint: `/ratingform/trigger/transaction/${idLegalTransaction}`,
        method: 'DELETE',
      }),
  });
  return {
    deleteTriggerByTransaction,
    isPendingDeleteTriggerByTransaction,
  };
};

export const useGetRatingTriggerEffective = (
  idLegalTransaction: number | null
) => {
  const enabled =
    idLegalTransaction !== null &&
    idLegalTransaction !== undefined &&
    idLegalTransaction > 0;
  const {
    data: triggerEffective,
    isLoading: isLoadingTriggerEffective,
    refetch: refetchTriggerEffective,
  } = useQuery<IRatingTriggerEffective>({
    queryKey: ['ratingform', 'trigger', 'effective', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/ratingform/trigger/effective/${idLegalTransaction}`,
      }),
    enabled,
  });
  return {
    triggerEffective,
    isLoadingTriggerEffective,
    refetchTriggerEffective,
  };
};

export const useGetRatingResponseFullObject = (
  idRatingResponse: number | null
) => {
  const enabled = idRatingResponse !== null && idRatingResponse !== undefined;
  const {
    data: response,
    isLoading: isLoadingResponse,
    refetch: refetchResponse,
  } = useQuery<IRatingResponseFullObject>({
    queryKey: ['ratingresponse', 'detail', idRatingResponse],
    queryFn: () =>
      customFetcher({ endpoint: `/ratingresponse/${idRatingResponse}` }),
    enabled,
  });
  return { response, isLoadingResponse, refetchResponse };
};
